import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * SMS segment math (UIUX §4.1) — pure and client-only.
 *
 * GSM-7 messages fit 160 chars in one segment, 153 per segment when
 * concatenated (7 bytes go to the UDH header). Any character outside the
 * GSM-7 alphabet forces UCS-2 encoding: 70 chars single / 67 concatenated.
 * GSM-7 "extension" characters (€, [, ], {, }, \, ^, |, ~) cost TWO septets
 * each — the classic gotcha that flips a 159-char draft into 2 segments.
 */

// Basic GSM 03.38 alphabet (single-septet characters).
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑܧ¿abcdefghijklmnopqrstuvwxyzäöñüà";
// Extension table characters — legal in GSM-7 but cost two septets.
const GSM7_EXTENDED = "^{}\\[~]|€";

const GSM7_SET = new Set(GSM7_BASIC);
const GSM7_EXT_SET = new Set(GSM7_EXTENDED);

export interface SmsSegmentInfo {
  encoding: "gsm7" | "ucs2";
  /** Character count as the carrier bills it (septets for GSM-7). */
  units: number;
  segments: number;
  /** Characters left before the NEXT segment is opened (0 when empty). */
  remainingInSegment: number;
}

/** Compute encoding, billed length and segment count for an SMS body. */
export function smsSegments(text: string): SmsSegmentInfo {
  let gsm7 = true;
  let septets = 0;
  // Iterate by code point so astral-plane emoji count once, not twice.
  for (const ch of text) {
    if (GSM7_SET.has(ch)) {
      septets += 1;
    } else if (GSM7_EXT_SET.has(ch)) {
      septets += 2;
    } else {
      gsm7 = false;
      break;
    }
  }
  if (gsm7) {
    const single = 160;
    const multi = 153;
    const segments =
      septets === 0 ? 0 : septets <= single ? 1 : Math.ceil(septets / multi);
    const capacity = segments <= 1 ? single : segments * multi;
    return {
      encoding: "gsm7",
      units: septets,
      segments,
      remainingInSegment: septets === 0 ? 0 : capacity - septets,
    };
  }
  const chars = Array.from(text).length;
  const single = 70;
  const multi = 67;
  const segments =
    chars === 0 ? 0 : chars <= single ? 1 : Math.ceil(chars / multi);
  const capacity = segments <= 1 ? single : segments * multi;
  return {
    encoding: "ucs2",
    units: chars,
    segments,
    remainingInSegment: chars === 0 ? 0 : capacity - chars,
  };
}

/**
 * Live counter for the SMS composer adapter row — "142 · 1 segment".
 * Amber at 2–3 segments, red above 3 (UIUX §4.1). Purely advisory:
 * the counter never blocks Send — the composer today has no separate
 * "send anyway" gate, the red tone IS the whole warning.
 */
export function SegmentCounter({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const info = smsSegments(text);
  if (info.units === 0) return null;
  const tone =
    info.segments > 3
      ? "text-destructive font-semibold"
      : info.segments >= 2
        ? "text-[hsl(var(--triage-warm-fg))] font-medium"
        : "text-muted-foreground";
  return (
    <span
      className={cn("text-[11px] tabular-nums", tone, className)}
      title={
        info.encoding === "ucs2"
          ? "Contains characters outside the basic SMS alphabet (emoji, smart quotes) — 70/67 chars per segment."
          : "Standard SMS alphabet — 160/153 chars per segment."
      }
      data-testid="sms-segment-counter"
    >
      {info.units} · {info.segments} segment{info.segments === 1 ? "" : "s"}
    </span>
  );
}
